import {
  apyToApr,
  calculateRateForCompoundType,
  TimeScale,
} from '../../../../utils/parsing'

/**
 * Parsed shape of the Kinetic Lens `MarketMetadata` struct returned by
 * `getMarketMetadataForAllMarkets()`. Rates are per-second (the Lens uses the
 * CToken's `*RatePerTimestamp` getters), and reward speeds are emitted as
 * parallel arrays keyed by reward index — we don't surface them through the
 * canonical single-speed slot.
 */
export interface KineticMarketMetadata {
  market: string // cToken address
  supplyRate: bigint // 1e18 scaled, per-second
  borrowRate: bigint // 1e18 scaled, per-second
  price: bigint // 1e(36 - underlyingDecimals) scaled
  exchangeRate: bigint // 1e(18 - cTokenDec + underlyingDec) scaled
  reserveFactor: bigint // 1e18 scaled
  borrowCap: bigint
  totalSupply: bigint // cToken-denominated
  totalUnderlyingSupply: bigint // underlying-denominated (already exchange-rate adjusted)
  totalBorrows: bigint
  collateralFactor: bigint // 1e18 scaled
  underlyingToken: string // address(0) for the native FLR market
  underlyingTokenDecimals: number
  cTokenDecimals: number
  totalReserves: bigint
  cash: bigint
  mintPaused: boolean
  borrowPaused: boolean
}

function toBigInt(value: unknown): bigint {
  if (typeof value === 'bigint') return value
  if (typeof value === 'number') return BigInt(value)
  if (typeof value === 'string') return BigInt(value)
  if (value && typeof value === 'object') {
    const v = value as any
    if (typeof v._hex === 'string') return BigInt(v._hex)
    if (typeof v.toString === 'function') return BigInt(v.toString())
  }
  throw new Error(`Cannot convert value to bigint: ${String(value)}`)
}

function toBool(value: unknown): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'bigint') return value !== 0n
  if (typeof value === 'number') return value !== 0
  return Boolean(value)
}

function field<T = unknown>(src: any, key: string, index: number): T {
  if (src == null) throw new Error('Source is null/undefined')
  if (key in src) return src[key]
  if (Array.isArray(src)) return src[index]
  return (src as any)[key]
}

/**
 * Struct order: market(0), supplyRate(1), borrowRate(2), price(3),
 * exchangeRate(4), reserveFactor(5), borrowCap(6), totalSupply(7),
 * totalUnderlyingSupply(8), totalBorrows(9), collateralFactor(10),
 * underlyingToken(11), underlyingTokenDecimals(12), cTokenDecimals(13),
 * supplyRewardSpeeds(14)[skipped], borrowRewardSpeeds(15)[skipped],
 * totalReserves(16), cash(17), mintPaused(18), borrowPaused(19),
 * accrualBlockTimestamp(20)[skipped]
 */
export function parseKineticMarketMetadata(input: any): KineticMarketMetadata {
  return {
    market: String(field(input, 'market', 0)),
    supplyRate: toBigInt(field(input, 'supplyRate', 1)),
    borrowRate: toBigInt(field(input, 'borrowRate', 2)),
    price: toBigInt(field(input, 'price', 3)),
    exchangeRate: toBigInt(field(input, 'exchangeRate', 4)),
    reserveFactor: toBigInt(field(input, 'reserveFactor', 5)),
    borrowCap: toBigInt(field(input, 'borrowCap', 6)),
    totalSupply: toBigInt(field(input, 'totalSupply', 7)),
    totalUnderlyingSupply: toBigInt(field(input, 'totalUnderlyingSupply', 8)),
    totalBorrows: toBigInt(field(input, 'totalBorrows', 9)),
    collateralFactor: toBigInt(field(input, 'collateralFactor', 10)),
    underlyingToken: String(field(input, 'underlyingToken', 11)),
    underlyingTokenDecimals: Number(
      field(input, 'underlyingTokenDecimals', 12),
    ),
    cTokenDecimals: Number(field(input, 'cTokenDecimals', 13)),
    totalReserves: toBigInt(field(input, 'totalReserves', 16)),
    cash: toBigInt(field(input, 'cash', 17)),
    mintPaused: toBool(field(input, 'mintPaused', 18)),
    borrowPaused: toBool(field(input, 'borrowPaused', 19)),
  }
}

/**
 * Returns deposit APR and borrow APR (percentages) for one Kinetic market.
 * The CToken implementation exposes per-timestamp rates (per-second), so we
 * compound at one-second granularity regardless of Flare's block time.
 */
export function getKineticRates(info: KineticMarketMetadata, chainId: string) {
  const supplyApy = calculateRateForCompoundType(
    info.supplyRate.toString(),
    chainId,
    TimeScale.SECOND,
  )
  const borrowApy = calculateRateForCompoundType(
    info.borrowRate.toString(),
    chainId,
    TimeScale.SECOND,
  )
  return {
    depositRate: apyToApr(supplyApy / 100) * 100,
    variableBorrowRate: apyToApr(borrowApy / 100) * 100,
  }
}
